import React from "react";
import { Navigate, useParams } from "react-router-dom";

import DatabaseSummaryStatsTable from "features/Dashboard/DatabaseSummaryStatsTable";
import Loading from "ui/Loading";
import PageContent from "ui/PageContent";
import PageSecondaryNavigation, {
  PageNavLink,
} from "ui/PageSecondaryNavigation";
import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";
import { useRoutes } from "utils/routes";

import { useOrganizationOverviewQuery } from "./gql/Query.generated";

import ServerGroupTable from "./ServerGroupTable";
import ServerTable from "./ServerTable";

type Props = {
  tab?: string;
};

const OrganizationOverview: React.FunctionComponent<Props> = ({ tab }) => {
  const { slug: organizationSlug } = useParams();
  const {
    organizationSetup,
    organizationDatabases,
    organizationServerGroups,
    organizationServers,
  } = useRoutes();
  const { data, loading, error } = useOrganizationOverviewQuery({
    variables: {
      organizationSlug,
    },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const {
    name,
    visibleServerCount,
    visibleServerGroupCount,
    permittedToCreateServers,
  } = data.getOrganizationDetails;

  if (visibleServerCount === 0 && permittedToCreateServers) {
    return <Navigate to={organizationSetup(organizationSlug)} replace />;
  }

  if (!tab || (tab === "servergroups" && visibleServerGroupCount === 0)) {
    return <Navigate to={organizationServers(organizationSlug)} replace />;
  }
  const featureNav =
    visibleServerCount > 0 ? (
      <PageSecondaryNavigation>
        {visibleServerGroupCount > 0 && (
          <PageNavLink to={organizationServerGroups(organizationSlug)}>
            All Server Groups
          </PageNavLink>
        )}
        <PageNavLink to={organizationServers(organizationSlug)}>
          All Servers
        </PageNavLink>
        <PageNavLink to={organizationDatabases(organizationSlug)}>
          All Databases
        </PageNavLink>
      </PageSecondaryNavigation>
    ) : null;

  return (
    <PageContent
      title={
        <span>
          Organization <strong>{name}</strong>
        </span>
      }
      featureNav={featureNav}
      windowTitle={`Organization: ${name}`}
      pageCategory="organization"
      pageName="show"
      pageTab={tab}
    >
      {tab === "servergroups" && (
        <ServerGroupTable organizationSlug={organizationSlug} />
      )}
      {tab === "servers" &&
        (visibleServerCount > 0 || permittedToCreateServers) && (
          <ServerTable organizationSlug={organizationSlug} />
        )}
      {tab === "databases" &&
        (visibleServerCount > 0 || permittedToCreateServers) && (
          <DatabaseSummaryStatsTable organizationSlug={organizationSlug} />
        )}
      {visibleServerCount === 0 && !permittedToCreateServers && (
        <Panel title="Restricted Permissions">
          <PanelSection>
            <p>
              You don't have access to any servers and are not allowed to create
              new ones.
            </p>
            <p>
              <strong>Hint:</strong> Ask your administrator to grant access, or
              explore the menu on the left for other organization actions, e.g.
              Billing.
            </p>
          </PanelSection>
        </Panel>
      )}
    </PageContent>
  );
};

export default OrganizationOverview;
